import { QueryClient } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import type { AppRouter, FeatureFlagWithState } from '@viapeople/api'
import superjson from 'superjson'
import { config } from '../config'
import { useFeatureFlagsStore } from '../stores/featureFlagsStore'

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			staleTime: 0,
		},
	},
})

const apiURL = config.apiURL.endsWith('/') ? config.apiURL : `${config.apiURL}/`

export const trpcClient = createTRPCClient<AppRouter>({
	links: [
		httpBatchLink({
			url: apiURL,
			fetch(url, options) {
				return fetch(url, {
					...options,
					credentials: 'include',
				}).then(async (response) => {
					// Extract feature flags from response headers
					const flagsHeader = response.headers.get('X-Feature-Flags')
					if (flagsHeader) {
						try {
							// Server encodeURIComponent's the header; this inner try/catch falls
							// back to the raw value so an old server (raw JSON) still parses while
							// the rollout is in flight. Safe to drop once all servers are upgraded.
							let decoded = flagsHeader
							try {
								decoded = decodeURIComponent(flagsHeader)
							} catch {
								// backward-compat: pre-encoding server, header is already raw JSON
							}
							const flags = JSON.parse(decoded) as FeatureFlagWithState[]
							useFeatureFlagsStore.getState().setFlags(flags)
						} catch (error) {
							console.error('Failed to parse feature flags from header:', error)
						}
					}

					// Don't auto-redirect for logout - let mutation handler manage it
					const urlString = typeof url === 'string' ? url : url.toString()
					if (response.status === 401 && !urlString.includes('auth.logout')) {
						window.location.href = '/auth/login'
					}
					return response
				})
			},
			transformer: superjson,
			headers: {
				'Content-Type': 'application/json',
			},
		}),
	],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient,
})
